import React, { useCallback, useState } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import { API_ENDPOINT } from "../../../utils/constants";
import { getHeader } from "../../../utils/util";

const FilterBuilder = ({ campaignPost, setCampaignPost, selectedGroup }) => {
  const fields = [
    { key: "Priority", label: "Priority", type: "number" },
    { key: "Job No", label: "Job No", type: "number" },
    { key: "Job Id", label: "Job ID", type: "number" },
  ];

  const operators = [
    { value: "$eq", label: "Equal (=)" },
    { value: "$ne", label: "Not Equal (≠)" },
    { value: "$gt", label: "Greater Than (>)" },
    { value: "$gte", label: "Greater Than or Equal (≥)" },
    { value: "$lt", label: "Less Than (<)" },
    { value: "$lte", label: "Less Than or Equal (≤)" },
  ];

  const [filters, setFilters] = useState(
    fields.map((field) => ({
      field: field.key,
      operator: "$eq",
      value: "",
      enabled: false,
      isRange: false,
      rangeStart: "",
      rangeEnd: "",
    }))
  );

  const [query, setQuery] = useState({});
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [resultExpanded, setResultExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(null);
  const [error, setError] = useState(null);

  const handleFilterChange = (index, key, value) => {
    const newFilters = [...filters];
    newFilters[index][key] = value;
    if (
      (key === "value" || key === "rangeStart" || key === "rangeEnd") &&
      value !== ""
    ) {
      newFilters[index].enabled = true;
    }
    setFilters(newFilters);
  };

  const toggleRange = (index) => {
    const newFilters = [...filters];
    newFilters[index].isRange = !newFilters[index].isRange;
    newFilters[index].value = "";
    newFilters[index].rangeStart = "";
    newFilters[index].rangeEnd = "";
    newFilters[index].enabled = false;
    setFilters(newFilters);
  };

  const generateQuery = async () => {
    const queryObj = {};

    filters.forEach((filter) => {
      if (filter.enabled) {
        const field = filter.field;
        const fieldDef = fields.find((f) => f.key === field);

        if (
          filter.isRange &&
          filter.rangeStart !== "" &&
          filter.rangeEnd !== ""
        ) {
          let start = filter.rangeStart;
          let end = filter.rangeEnd;

          if (fieldDef.type === "number") {
            start = Number(start);
            end = Number(end);
          }

          queryObj[field] = {
            $gte: start,
            $lte: end,
          };
        } else if (!filter.isRange && filter.value !== "") {
          const operator = filter.operator;
          let value = filter.value;

          if (fieldDef.type === "number") {
            value = Number(value);
          }

          if (operator === "$eq") {
            queryObj[field] = value;
          } else {
            queryObj[field] = { [operator]: value };
          }
        }
      }
    });

    setQuery(queryObj);
    setCampaignPost((prev) => ({ ...prev, marketingQuery: queryObj }));
    setResultExpanded(true);

    // Fetch count from API
    setLoading(true);
    setError(null);
    setCount(null);

    try {
      const response = await axios.post(
        `${API_ENDPOINT}/database/get-filter-count`,
        {
          query: { ...queryObj, subCategory: selectedGroup?.name },
        },
        getHeader()
      );
      setCount(response.data.count);
      setCampaignPost((prev) => ({
        ...prev,
        contactsCount: response.data.count,
      }));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch count");
      console.error("Error fetching count:", err);
    } finally {
      setLoading(false);
    }
  };
  const handleApplyQuery = useCallback(() => {
    setCampaignPost((prev) => ({ ...prev, marketingQuery: query }));
  }, [query]);
  const clearFilters = () => {
    setFilters(
      fields.map((field) => ({
        field: field.key,
        operator: "$eq",
        value: "",
        enabled: false,
        isRange: false,
        rangeStart: "",
        rangeEnd: "",
      }))
    );
    setQuery({});
    setCount(null);
    setError(null);
  };

  const activeFiltersCount = filters.filter((f) => f.enabled).length;

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
      <Accordion
        expanded={filterExpanded}
        onChange={() => setFilterExpanded(!filterExpanded)}
        sx={{ mb: 2 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ bgcolor: "#f5f5f5" }}
        >
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Filter Builder
          </Typography>
          {activeFiltersCount > 0 && (
            <Chip
              label={`${activeFiltersCount} Active`}
              color="primary"
              size="small"
              sx={{ mr: 2 }}
            />
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={3}>
            {filters.map((filter, index) => {
              const field = fields.find((f) => f.key === filter.field);
              return (
                <Box
                  key={filter.field}
                  sx={{
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                    bgcolor: filter.enabled ? "#f0f9ff" : "white",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography sx={{ minWidth: 80, fontWeight: 600 }}>
                      {field.label}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={filter.isRange}
                          onChange={() => toggleRange(index)}
                        />
                      }
                      label="Range"
                      sx={{ ml: 2 }}
                    />
                    {filter.enabled && (
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                        sx={{ ml: "auto" }}
                        onDelete={() => {
                          const newFilters = [...filters];
                          newFilters[index].enabled = false;
                          newFilters[index].value = "";
                          newFilters[index].rangeStart = "";
                          newFilters[index].rangeEnd = "";
                          setFilters(newFilters);
                        }}
                      />
                    )}
                  </Box>

                  {filter.isRange ? (
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <TextField
                        size="small"
                        type={field.type}
                        label="From"
                        value={filter.rangeStart}
                        onChange={(e) =>
                          handleFilterChange(
                            index,
                            "rangeStart",
                            e.target.value
                          )
                        }
                        sx={{ flex: 1 }}
                      />
                      <Typography sx={{ color: "text.secondary" }}>
                        to
                      </Typography>
                      <TextField
                        size="small"
                        type={field.type}
                        label="To"
                        value={filter.rangeEnd}
                        onChange={(e) =>
                          handleFilterChange(index, "rangeEnd", e.target.value)
                        }
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Operator</InputLabel>
                        <Select
                          value={filter.operator}
                          label="Operator"
                          onChange={(e) =>
                            handleFilterChange(
                              index,
                              "operator",
                              e.target.value
                            )
                          }
                        >
                          {operators.map((op) => (
                            <MenuItem key={op.value} value={op.value}>
                              {op.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <TextField
                        size="small"
                        type={field.type}
                        label="Value"
                        value={filter.value}
                        onChange={(e) =>
                          handleFilterChange(index, "value", e.target.value)
                        }
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}

            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={generateQuery}
                disabled={loading}
                sx={{ flex: 1 }}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? "Fetching..." : "Generate Query"}
              </Button>
              <Button variant="outlined" onClick={handleApplyQuery}>
                Apply
              </Button>
              <Button
                variant="outlined"
                onClick={clearFilters}
                disabled={loading}
              >
                Clear All
              </Button>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Query Result Accordion */}
      {Object.keys(query).length > 0 && (
        <Accordion
          expanded={resultExpanded}
          onChange={() => setResultExpanded(!resultExpanded)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ bgcolor: "#e3f2fd" }}
          >
            <Typography variant="h6">Query Result</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              {/* Loading State */}
              {loading && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    p: 3,
                    bgcolor: "#f5f5f5",
                    borderRadius: 2,
                  }}
                >
                  <CircularProgress size={24} />
                  <Typography variant="body1" color="text.secondary">
                    Fetching count...
                  </Typography>
                </Box>
              )}

              {/* Error State */}
              {error && !loading && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Count Display */}
              {count !== null && !loading && !error && (
                <Box
                  sx={{
                    p: 2,
                    mt: 1,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: 2,
                    textAlign: "center",
                    boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                  }}
                >
                  <Typography
                    variant="h2"
                    sx={{ color: "white", fontWeight: "bold", mb: 1 }}
                  >
                    {count.toLocaleString()}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ color: "rgba(255, 255, 255, 0.95)" }}
                  >
                    {count === 1 ? "Record Found" : "Records Found"}
                  </Typography>
                </Box>
              )}

              <Typography
                mt={2}
                variant="subtitle1"
                gutterBottom
                fontWeight="bold"
              >
                Query Object:
              </Typography>
              <Box
                component="pre"
                sx={{
                  bgcolor: "#1e1e1e",
                  color: "#d4d4d4",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                  fontSize: "14px",
                  fontFamily: "monospace",
                  mb: 3,
                }}
              >
                {JSON.stringify(query, null, 2)}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default FilterBuilder;
